import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';

const FriendsAndSocial = ({ initialTab }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState(initialTab || 'friends');

  // Update sub-tab when initialTab prop changes
  useEffect(() => {
    if (initialTab && ['friends', 'requests', 'activity', 'find'].includes(initialTab)) {
      setActiveSubTab(initialTab);
    }
  }, [initialTab]);

  // Friends data
  const [friends, setFriends] = useState([]);
  const [friendsStats, setFriendsStats] = useState({ online: 0, playing: 0, away: 0, offline: 0 });

  // Friend requests data
  const [pendingRequests, setPendingRequests] = useState({ received: [], sent: [] });

  // Activity feed data
  const [activityFeed, setActivityFeed] = useState([]);

  // Search and add friend
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // UI state
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadFriendsData();
      loadPendingRequests();
      loadActivityFeed();
    }
  }, [user]);

  const loadFriendsData = async () => {
    try {
      setLoading(true);

      const { data: friendships, error: friendsError } = await supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user.id);

      if (friendsError) throw friendsError;

      if (!friendships || friendships.length === 0) {
        setFriends([]);
        setFriendsStats({ online: 0, playing: 0, away: 0, offline: 0 });
        return;
      }

      const friendIds = friendships.map(f => f.friend_id);

      // Get friend profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, avatar_url')
        .in('id', friendIds);

      if (profilesError) throw profilesError;

      // Get presence data
      const { data: presenceData, error: presenceError } = await supabase
        .from('user_presence')
        .select(`
          user_id,
          status,
          game_id,
          custom_status,
          joinable,
          last_seen,
          game:game_id (
            id,
            title,
            cover_image_url
          )
        `)
        .in('user_id', friendIds);

      if (presenceError) throw presenceError;

      // Create presence map
      const presenceMap = {};
      if (presenceData) {
        presenceData.forEach(p => {
          presenceMap[p.user_id] = {
            status: p.status,
            gameId: p.game_id,
            gameName: p.game?.title || null,
            gameThumbnail: p.game?.cover_image_url || null,
            customStatus: p.custom_status,
            joinable: p.joinable,
            lastSeen: p.last_seen
          };
        });
      }

      // Combine profile and presence
      let friendsList = (profiles || []).map(profile => {
        const presence = presenceMap[profile.id] || {
          status: 'offline',
          gameId: null,
          gameName: null,
          gameThumbnail: null,
          customStatus: null,
          joinable: false,
          lastSeen: null
        };

        return {
          id: profile.id,
          email: profile.email,
          fullName: profile.full_name,
          avatarUrl: profile.avatar_url,
          presence: presence
        };
      });

      // Sort by status
      friendsList.sort((a, b) => {
        const statusOrder = { playing: 0, online: 1, away: 2, offline: 3 };
        const aOrder = statusOrder[a.presence.status] || 3;
        const bOrder = statusOrder[b.presence.status] || 3;

        if (aOrder !== bOrder) return aOrder - bOrder;

        const aTime = new Date(a.presence.lastSeen || 0).getTime();
        const bTime = new Date(b.presence.lastSeen || 0).getTime();
        return bTime - aTime;
      });

      setFriends(friendsList);

      // Calculate stats
      const stats = {
        online: friendsList.filter(f => f.presence.status === 'online').length,
        playing: friendsList.filter(f => f.presence.status === 'playing').length,
        away: friendsList.filter(f => f.presence.status === 'away').length,
        offline: friendsList.filter(f => f.presence.status === 'offline').length
      };
      setFriendsStats(stats);

    } catch (err) {
      console.error('Error loading friends:', err);
      setError('Failed to load friends list');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    try {
      // Get received requests
      const { data: receivedRequests, error: receivedError } = await supabase
        .from('friend_requests')
        .select('id, from_user_id, message, created_at')
        .eq('to_user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (receivedError) throw receivedError;

      // Get profiles for received requests
      const receivedUserIds = receivedRequests?.map(req => req.from_user_id) || [];
      const { data: receivedProfiles } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, avatar_url')
        .in('id', receivedUserIds);

      const receivedProfileMap = (receivedProfiles || []).reduce((acc, profile) => {
        acc[profile.id] = profile;
        return acc;
      }, {});

      // Get sent requests
      const { data: sentRequests, error: sentError } = await supabase
        .from('friend_requests')
        .select('id, to_user_id, message, created_at')
        .eq('from_user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (sentError) throw sentError;

      // Get profiles for sent requests
      const sentUserIds = sentRequests?.map(req => req.to_user_id) || [];
      const { data: sentProfiles } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, avatar_url')
        .in('id', sentUserIds);

      const sentProfileMap = (sentProfiles || []).reduce((acc, profile) => {
        acc[profile.id] = profile;
        return acc;
      }, {});

      const formattedReceived = (receivedRequests || []).map(req => {
        const profile = receivedProfileMap[req.from_user_id];
        return {
          id: req.id,
          fromUser: {
            id: profile?.id,
            email: profile?.email,
            fullName: profile?.full_name,
            avatarUrl: profile?.avatar_url
          },
          message: req.message,
          createdAt: req.created_at
        };
      });

      const formattedSent = (sentRequests || []).map(req => {
        const profile = sentProfileMap[req.to_user_id];
        return {
          id: req.id,
          toUser: {
            id: profile?.id,
            email: profile?.email,
            fullName: profile?.full_name,
            avatarUrl: profile?.avatar_url
          },
          message: req.message,
          createdAt: req.created_at
        };
      });

      setPendingRequests({
        received: formattedReceived,
        sent: formattedSent
      });

    } catch (err) {
      console.error('Error loading friend requests:', err);
    }
  };

  const loadActivityFeed = async () => {
    try {
      // Get friend IDs
      const { data: friendships } = await supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user.id);

      if (!friendships || friendships.length === 0) {
        setActivityFeed([]);
        return;
      }

      const friendIds = friendships.map(f => f.friend_id);

      // Get activity from materialized view
      const { data: activities, error: activitiesError } = await supabase
        .from('friend_activity_feed')
        .select('*')
        .in('user_id', friendIds)
        .order('created_at', { ascending: false })
        .limit(20);

      if (activitiesError) throw activitiesError;

      const events = (activities || []).map(activity => ({
        id: activity.id,
        userId: activity.user_id,
        email: activity.email,
        fullName: activity.full_name,
        avatarUrl: activity.avatar_url,
        eventType: activity.event_type,
        gameId: activity.game_id,
        gameName: activity.game_title,
        gameThumbnail: activity.game_thumbnail,
        metadata: activity.metadata,
        createdAt: activity.created_at
      }));

      setActivityFeed(events);

    } catch (err) {
      console.error('Error loading activity feed:', err);
    }
  };

  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearching(true);
      setError(null);

      // Search by email or full name
      const { data: users, error: searchError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, avatar_url')
        .or(`email.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%`)
        .neq('id', user.id)
        .limit(10);

      if (searchError) throw searchError;

      // Filter out existing friends
      const friendIds = friends.map(f => f.id);
      const filteredUsers = (users || []).filter(u => !friendIds.includes(u.id));

      setSearchResults(filteredUsers);

    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users');
    } finally {
      setSearching(false);
    }
  };

  const handleSendFriendRequest = async (toUserId, message = '') => {
    try {
      setError(null);

      // Call the API endpoint instead of direct insert (has validation logic)
      const response = await fetch('/api/sdk/friends/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fromUserId: user.id,
          toUserId: toUserId,
          message: message
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send friend request');
      }

      // Check if it was auto-accepted
      if (result.autoAccepted) {
        setSuccess('Friend request accepted - you are now friends!');
      } else {
        setSuccess('Friend request sent!');
      }

      setSearchQuery('');
      setSearchResults([]);
      loadPendingRequests();

      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error sending friend request:', err);
      setError(err.message || 'Failed to send friend request');
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      setError(null);

      // Call the API endpoint instead of direct update (triggers database functions)
      const response = await fetch('/api/sdk/friends/accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requestId: requestId,
          userId: user.id
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to accept friend request');
      }

      setSuccess('Friend request accepted!');
      loadFriendsData();
      loadPendingRequests();
      loadActivityFeed();

      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error accepting friend request:', err);
      setError(err.message || 'Failed to accept request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      setError(null);

      // Call the API endpoint instead of direct update
      const response = await fetch('/api/sdk/friends/decline', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requestId: requestId,
          userId: user.id
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to decline friend request');
      }

      loadPendingRequests();

    } catch (err) {
      console.error('Error declining friend request:', err);
      setError(err.message || 'Failed to decline request');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!confirm('Are you sure you want to remove this friend?')) return;

    try {
      setError(null);

      // Remove both directions of friendship
      const { error: removeError } = await supabase
        .from('friendships')
        .delete()
        .or(`and(user_id.eq.${user.id},friend_id.eq.${friendId}),and(user_id.eq.${friendId},friend_id.eq.${user.id})`);

      if (removeError) throw removeError;

      setSuccess('Friend removed');
      loadFriendsData();
      loadActivityFeed();

      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error removing friend:', err);
      setError('Failed to remove friend');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'playing': return 'bg-blue-500';
      case 'away': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return 'Circle';
      case 'playing': return 'Gamepad2';
      case 'away': return 'Clock';
      case 'offline': return 'CircleSlash';
      default: return 'Circle';
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'achievement_unlocked': return 'Award';
      case 'high_score': return 'Trophy';
      case 'game_started': return 'Play';
      case 'game_completed': return 'CheckCircle';
      default: return 'Activity';
    }
  };

  const getEventText = (event) => {
    switch (event.eventType) {
      case 'achievement_unlocked':
        return `unlocked "${event.metadata?.achievementName || 'achievement'}"`;
      case 'high_score':
        return `scored ${event.metadata?.score?.toLocaleString() || 0} points`;
      case 'game_started':
        return 'started playing';
      case 'game_completed':
        return 'completed the game';
      default:
        return event.eventType;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start">
          <Icon name="AlertCircle" size={20} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-500 font-medium">Error</p>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-start">
          <Icon name="CheckCircle" size={20} className="text-green-500 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-green-500 font-medium">{success}</p>
        </div>
      )}

      {/* Sub-navigation */}
      <div className="bg-card border border-border rounded-lg p-1 flex gap-1">
        <button
          onClick={() => setActiveSubTab('friends')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeSubTab === 'friends'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="Users" size={16} />
            <span>Friends ({friends.length})</span>
          </div>
        </button>
        <button
          onClick={() => setActiveSubTab('requests')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
            activeSubTab === 'requests'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="UserPlus" size={16} />
            <span>Requests</span>
            {pendingRequests.received.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {pendingRequests.received.length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveSubTab('activity')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeSubTab === 'activity'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="Activity" size={16} />
            <span>Activity</span>
          </div>
        </button>
        <button
          onClick={() => setActiveSubTab('search')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeSubTab === 'search'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="Search" size={16} />
            <span>Find Friends</span>
          </div>
        </button>
      </div>

      {/* Friends List */}
      {activeSubTab === 'friends' && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-500">{friendsStats.playing}</div>
              <div className="text-sm text-muted-foreground">Playing</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-500">{friendsStats.online}</div>
              <div className="text-sm text-muted-foreground">Online</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-500">{friendsStats.away}</div>
              <div className="text-sm text-muted-foreground">Away</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-500">{friendsStats.offline}</div>
              <div className="text-sm text-muted-foreground">Offline</div>
            </div>
          </div>

          {/* Friends list */}
          {friends.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No friends yet</h3>
              <p className="text-muted-foreground mb-4">
                Start building your gaming network by adding friends!
              </p>
              <button
                onClick={() => setActiveSubTab('search')}
                className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="Search" size={16} className="mr-2" />
                Find Friends
              </button>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg divide-y divide-border">
              {friends.map(friend => (
                <div key={friend.id} className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors">
                  {/* Avatar */}
                  <div className="relative">
                    <img
                      src={friend.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(friend.fullName)}&background=6366f1&color=fff`}
                      alt={friend.fullName}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card ${getStatusColor(friend.presence.status)}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground truncate">{friend.fullName}</h4>
                      <Icon name={getStatusIcon(friend.presence.status)} size={14} className="text-muted-foreground flex-shrink-0" />
                    </div>

                    {friend.presence.status === 'playing' && friend.presence.gameName && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="Gamepad2" size={14} />
                        <span className="truncate">Playing {friend.presence.gameName}</span>
                        {friend.presence.joinable && (
                          <span className="text-xs bg-green-500/20 text-green-500 px-2 py-0.5 rounded">Joinable</span>
                        )}
                      </div>
                    )}

                    {friend.presence.customStatus && (
                      <p className="text-sm text-muted-foreground truncate">{friend.presence.customStatus}</p>
                    )}

                    {friend.presence.status === 'offline' && (
                      <p className="text-sm text-muted-foreground">Last seen {formatTimeAgo(friend.presence.lastSeen)}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {friend.presence.status === 'playing' && friend.presence.gameId && (
                      <a
                        href={`/games/${friend.presence.gameId}`}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                        title="View game"
                      >
                        <Icon name="ExternalLink" size={18} />
                      </a>
                    )}
                    <button
                      onClick={() => handleRemoveFriend(friend.id)}
                      className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Remove friend"
                    >
                      <Icon name="UserMinus" size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Friend Requests */}
      {activeSubTab === 'requests' && (
        <div className="space-y-6">
          {/* Received Requests */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Inbox" size={20} />
              Received Requests ({pendingRequests.received.length})
            </h3>

            {pendingRequests.received.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No pending friend requests</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg divide-y divide-border">
                {pendingRequests.received.map(request => (
                  <div key={request.id} className="p-4 flex items-center gap-4">
                    <img
                      src={request.fromUser.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(request.fromUser.fullName)}&background=6366f1&color=fff`}
                      alt={request.fromUser.fullName}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground">{request.fromUser.fullName}</h4>
                      {request.message && (
                        <p className="text-sm text-muted-foreground">{request.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{formatTimeAgo(request.createdAt)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAcceptRequest(request.id)}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center gap-2"
                      >
                        <Icon name="Check" size={16} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleDeclineRequest(request.id)}
                        className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg font-medium hover:bg-red-500/20 transition-colors flex items-center gap-2"
                      >
                        <Icon name="X" size={16} />
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sent Requests */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Send" size={20} />
              Sent Requests ({pendingRequests.sent.length})
            </h3>

            {pendingRequests.sent.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <Icon name="Send" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No pending sent requests</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg divide-y divide-border">
                {pendingRequests.sent.map(request => (
                  <div key={request.id} className="p-4 flex items-center gap-4">
                    <img
                      src={request.toUser.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(request.toUser.fullName)}&background=6366f1&color=fff`}
                      alt={request.toUser.fullName}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground">{request.toUser.fullName}</h4>
                      {request.message && (
                        <p className="text-sm text-muted-foreground">{request.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">Sent {formatTimeAgo(request.createdAt)}</p>
                    </div>
                    <div className="text-sm text-yellow-500 flex items-center gap-2">
                      <Icon name="Clock" size={16} />
                      Pending
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity Feed */}
      {activeSubTab === 'activity' && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Activity" size={20} />
            Friend Activity (Last 7 Days)
          </h3>

          {activityFeed.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No recent activity from friends</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg divide-y divide-border">
              {activityFeed.map(event => (
                <div key={event.id} className="p-4 flex items-start gap-4 hover:bg-muted/50 transition-colors">
                  <img
                    src={event.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(event.fullName)}&background=6366f1&color=fff`}
                    alt={event.fullName}
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-foreground">{event.fullName}</span>
                      <Icon name={getEventIcon(event.eventType)} size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{getEventText(event)}</span>
                    </div>
                    {event.gameName && (
                      <div className="flex items-center gap-2 mt-1">
                        {event.gameThumbnail && (
                          <img src={event.gameThumbnail} alt={event.gameName} className="w-6 h-6 rounded" />
                        )}
                        <a
                          href={`/games/${event.gameId}`}
                          className="text-sm text-primary hover:underline"
                        >
                          {event.gameName}
                        </a>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{formatTimeAgo(event.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Find Friends */}
      {activeSubTab === 'search' && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Search" size={20} />
            Find Friends
          </h3>

          {/* Search Form */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchUsers()}
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={handleSearchUsers}
                disabled={searching || !searchQuery.trim()}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {searching ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Icon name="Search" size={16} />
                    Search
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="bg-card border border-border rounded-lg divide-y divide-border">
              {searchResults.map(user => (
                <div key={user.id} className="p-4 flex items-center gap-4">
                  <img
                    src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name)}&background=6366f1&color=fff`}
                    alt={user.full_name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-foreground">{user.full_name}</h4>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <button
                    onClick={() => handleSendFriendRequest(user.id, '')}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
                  >
                    <Icon name="UserPlus" size={16} />
                    Add Friend
                  </button>
                </div>
              ))}
            </div>
          )}

          {searchResults.length === 0 && searchQuery && !searching && (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="SearchX" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No users found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FriendsAndSocial;
